/**
 * mSTEM Robot Manager Modal container (GEN-03). Owns local edit/create
 * state (editingRobot, isCreating); dispatches to robotsReducer; mirrors
 * each write to localStorage via writeRobots so the data persists across
 * page reloads.
 *
 * Persistence pattern (T-02-03-02): the helper persistAfterDispatch
 * computes the next list locally from the current Redux snapshot BEFORE
 * dispatching. This avoids races with redux-throttle (300ms coalesce
 * window applied to all dispatches at the store middleware layer).
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import {closeRobotManager} from '../reducers/modals';
import {addRobot, updateRobot, deleteRobot, seedRobots} from '../reducers/robots';
import {readRobots, writeRobots} from '../lib/mstem-storage';

import RobotManagerModalComponent from '../components/robot-manager-modal/robot-manager-modal.jsx';

const confirmMessages = defineMessages({
    confirmDelete: {
        defaultMessage: 'Xoá robot này khỏi danh sách?',
        description: 'Confirm dialog for deleting a robot',
        id: 'gen.robot.confirm.delete'
    }
});

class RobotManagerModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickAdd',
            'handleClickEdit',
            'handleClickDelete',
            'handleClickConnect',
            'handleCloseConnect',
            'handleSearch',
            'handleSubmitForm',
            'handleCancelForm',
            'handleRequestClose'
        ]);
        this.state = {
            editingRobot: null,
            isCreating: false,
            connectingRobot: null,
            searchTerm: ''
        };
    }
    componentDidMount () {
        // Hydrate from localStorage on first mount (only if stored data exists).
        const stored = readRobots();
        if (stored && Array.isArray(stored) && stored.length > 0) {
            this.props.onSeedRobots(stored);
        }
    }
    persistAfterDispatch (mutator) {
        // Compute next list locally so writeRobots fires with the expected
        // post-dispatch shape — independent of redux-throttle coalescing.
        const nextList = mutator(this.props.robots);
        writeRobots(nextList);
    }
    handleClickAdd () {
        this.setState({isCreating: true, editingRobot: null});
    }
    handleClickEdit (robot) {
        this.setState({editingRobot: robot, isCreating: false});
    }
    handleClickDelete (id) {
        const msg = this.props.intl.formatMessage(confirmMessages.confirmDelete);
        // eslint-disable-next-line no-alert
        if (typeof window !== 'undefined' && window.confirm && !window.confirm(msg)) {
            return;
        }
        this.persistAfterDispatch(list => list.filter(r => r.id !== id));
        this.props.onDeleteRobot(id);
    }
    handleSubmitForm (robot) {
        if (this.state.isCreating) {
            this.persistAfterDispatch(list => list.concat([robot]));
            this.props.onAddRobot(robot);
        } else {
            this.persistAfterDispatch(list => list.map(r => (
                r.id === robot.id ? Object.assign({}, r, robot) : r
            )));
            this.props.onUpdateRobot(robot);
        }
        this.setState({editingRobot: null, isCreating: false});
    }
    handleCancelForm () {
        this.setState({editingRobot: null, isCreating: false});
    }
    handleClickConnect (robot) {
        // MOCK-ONLY: open the reusable connection wizard for this robot.
        this.setState({connectingRobot: robot});
    }
    handleCloseConnect () {
        this.setState({connectingRobot: null});
    }
    handleSearch (e) {
        this.setState({searchTerm: e.target.value});
    }
    handleRequestClose () {
        this.setState({editingRobot: null, isCreating: false, connectingRobot: null, searchTerm: ''});
        this.props.onClose();
    }
    render () {
        return (
            <RobotManagerModalComponent
                connectingRobot={this.state.connectingRobot}
                editingRobot={this.state.editingRobot}
                isCreating={this.state.isCreating}
                robots={this.props.robots}
                searchTerm={this.state.searchTerm}
                onCancelForm={this.handleCancelForm}
                onClickAdd={this.handleClickAdd}
                onClickConnect={this.handleClickConnect}
                onClickDelete={this.handleClickDelete}
                onClickEdit={this.handleClickEdit}
                onCloseConnect={this.handleCloseConnect}
                onRequestClose={this.handleRequestClose}
                onSearch={this.handleSearch}
                onSubmitForm={this.handleSubmitForm}
            />
        );
    }
}

RobotManagerModal.propTypes = {
    intl: intlShape.isRequired,
    onAddRobot: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onDeleteRobot: PropTypes.func.isRequired,
    onSeedRobots: PropTypes.func.isRequired,
    onUpdateRobot: PropTypes.func.isRequired,
    robots: PropTypes.arrayOf(PropTypes.object).isRequired // eslint-disable-line react/forbid-prop-types
};

const mapStateToProps = state => ({
    robots: state.scratchGui.robots.list
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeRobotManager()),
    onAddRobot: robot => dispatch(addRobot(robot)),
    onUpdateRobot: robot => dispatch(updateRobot(robot)),
    onDeleteRobot: id => dispatch(deleteRobot(id)),
    onSeedRobots: list => dispatch(seedRobots(list))
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(RobotManagerModal);
